import React from "react";
import { Redirect, useHistory } from "react-router-dom";
import { goBackInApp } from "../../domains/app/utils/backNavigation";
import { useApiQuery } from "../../services/api";
import { BackButton } from "../../components/ui/BackButton";

// Lean public landing for a Work — a paper Juisci holds metadata for but has NOT
// published as an Article (surfaced from an Evidence GPT citation). Shows just
// enough to be a citable source: identity, abstract/conclusion (original
// language), the day-0 quality score as a CONFIDENCE signal, and a link to the
// original. If the paper IS published as an Article the endpoint returns
// `{ kind: "article", slug }` and we CLIENT-redirect to /post/<slug> — a server
// 302 would be swallowed by the useApiQuery fetch (the browser never navigates).
//
// The page has no layout wrapper, so it owns its own back control (`Frame`) —
// without one it is a dead end inside the native webview shells (no browser
// chrome): reached from an Evidence GPT citation, there was no way back to the
// conversation.

// Shared page chrome for every state of the page: the safe-area padding this
// standalone page applies itself, plus the back control.
const Frame: React.FC<{
  onExit: () => void;
  centered: boolean;
  children: React.ReactNode;
}> = ({ onExit, centered, children }) => (
  <div
    style={{
      maxWidth: 720,
      margin: "0 auto",
      padding:
        "calc(24px + var(--safe-area-top)) 24px calc(24px + var(--safe-area-bottom))",
    }}
  >
    <BackButton onClick={onExit} className="mb-4" />
    <div style={centered ? { textAlign: "center" } : undefined}>{children}</div>
  </div>
);

const WorkPage: React.FC<{ workId: string }> = ({ workId }) => {
  const history = useHistory();
  const query = useApiQuery("/webapp/public/works/:id", {
    param: { id: workId },
  });

  // Back to where the user came from (the Evidence GPT answer, typically). A
  // cold entry (shared link, hard reload) has no page of ours behind it, so it
  // lands on discovery instead.
  function exitWork() {
    goBackInApp(history, "/discovery");
  }

  if (query.state === "pending")
    return (
      <Frame onExit={exitWork} centered>
        Loading…
      </Frame>
    );
  if (query.state !== "success")
    return (
      <Frame onExit={exitWork} centered>
        This source could not be found.
      </Frame>
    );

  const body = query.body;
  if (body === undefined)
    return (
      <Frame onExit={exitWork} centered>
        This source could not be found.
      </Frame>
    );
  if (body.kind === "article") return <Redirect to={`/post/${body.slug}`} />;

  const work = body.work;
  const meta = [work.journal, work.year === null ? null : String(work.year)]
    .filter((part) => part !== null)
    .join(" · ");
  const confidencePct =
    work.confidenceScore === null
      ? null
      : `${String(Math.round(work.confidenceScore * 100))}%`;

  return (
    <Frame onExit={exitWork} centered={false}>
      <div
        style={{
          display: "flex",
          gap: 8,
          alignItems: "center",
          flexWrap: "wrap",
          fontSize: 13,
          color: "#666",
        }}
      >
        {meta !== "" && <span>{meta}</span>}
        {work.articleType !== null && (
          <span
            style={{
              border: "1px solid #ddd",
              borderRadius: 999,
              padding: "1px 8px",
            }}
          >
            {work.articleType}
          </span>
        )}
        {confidencePct !== null && (
          <span
            title="Day-0 quality score"
            style={{
              background: "#eef6ff",
              color: "#0b5cad",
              borderRadius: 999,
              padding: "1px 8px",
            }}
          >
            Confidence {confidencePct}
          </span>
        )}
      </div>

      <h1 style={{ fontSize: 22, lineHeight: 1.3, marginTop: 12 }}>
        {work.title}
      </h1>
      {work.authors.length > 0 && (
        <div style={{ color: "#666", fontSize: 14 }}>
          {work.authors.join(", ")}
        </div>
      )}

      {work.abstract !== null && (
        <section style={{ marginTop: 20 }}>
          <h2
            style={{ fontSize: 15, textTransform: "uppercase", color: "#888" }}
          >
            Abstract
          </h2>
          <p style={{ whiteSpace: "pre-wrap" }}>{work.abstract}</p>
        </section>
      )}

      {work.doiUrl !== null && (
        <a
          href={work.doiUrl}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            display: "inline-block",
            marginTop: 24,
            padding: "10px 18px",
            background: "#0b5cad",
            color: "#fff",
            borderRadius: 8,
            textDecoration: "none",
          }}
        >
          Read the original ↗
        </a>
      )}
    </Frame>
  );
};

export default WorkPage;
